/**
 * Locks document scroll while the mobile facet filter drawer is open.
 * Used on Graphene collection/search PLPs where overflow classes alone are insufficient.
 *
 * Also persists “keep drawer open” across GrapheneHC result re-renders so shoppers can
 * select multiple filter options without reopening Filters (search was losing the open
 * state because the disclosure node is replaced before success runs).
 */
(function () {
  let savedScrollY = 0;
  let preventHandler = null;

  const LOCK_CLASS = 'facet-filters-drawer-open';
  const LOCKED_CLASS = 'is-facet-drawer-scroll-locked';
  const MOBILE_OVERFLOW_CLASS = 'overflow-hidden-mobile';
  const KEEP_OPEN_KEY = '__keepFacetFiltersDrawerOpen';

  const getDisclosure = () =>
    document.querySelector('menu-drawer.mobile-facets__wrapper .mobile-facets__disclosure') ||
    document.querySelector('.mobile-facets__wrapper .mobile-facets__disclosure');

  const isScrollAllowedTarget = (target) =>
    Boolean(
      target?.closest?.(
        '.mobile-facets__wrapper, .mobile-facets, .mobile-facets__inner, .mobile-facets__main, .mobile-facets__submenu'
      )
    );

  const preventBackgroundScroll = (event) => {
    if (!document.body.classList.contains(LOCK_CLASS)) return;
    if (isScrollAllowedTarget(event.target)) return;
    event.preventDefault();
  };

  const removeHiddenHeaderClass = () => {
    document.querySelector('.section-header')?.classList.remove('shopify-section-header-hidden');
  };

  const isFacetFiltersDrawerOpen = () => {
    if (window[KEEP_OPEN_KEY]) return true;
    const disclosure = getDisclosure();
    return Boolean(
      disclosure &&
      (disclosure.hasAttribute('open') || disclosure.classList.contains('menu-opening'))
    );
  };

  const applyLock = () => {
    savedScrollY = window.scrollY;

    document.documentElement.classList.add(LOCK_CLASS, LOCKED_CLASS, MOBILE_OVERFLOW_CLASS);
    document.body.classList.add(LOCK_CLASS, LOCKED_CLASS, MOBILE_OVERFLOW_CLASS);

    document.body.style.setProperty('position', 'fixed', 'important');
    document.body.style.setProperty('top', `0`, 'important');
    document.body.style.setProperty('left', '0', 'important');
    document.body.style.setProperty('right', '0', 'important');
    document.body.style.setProperty('width', '100%', 'important');

    if (!preventHandler) {
      preventHandler = preventBackgroundScroll;
      document.addEventListener('wheel', preventHandler, { passive: false, capture: true });
      document.addEventListener('touchmove', preventHandler, { passive: false, capture: true });
    }

    document.documentElement.dataset.facetDrawerScrollLocked = 'true';
  };

  const releaseLock = () => {
    document.documentElement.classList.remove(LOCK_CLASS, LOCKED_CLASS, MOBILE_OVERFLOW_CLASS);
    document.body.classList.remove(LOCK_CLASS, LOCKED_CLASS, MOBILE_OVERFLOW_CLASS);

    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('left');
    document.body.style.removeProperty('right');
    document.body.style.removeProperty('width');

    if (preventHandler) {
      document.removeEventListener('wheel', preventHandler, { capture: true });
      document.removeEventListener('touchmove', preventHandler, { capture: true });
      preventHandler = null;
    }

    delete document.documentElement.dataset.facetDrawerScrollLocked;
    delete document.documentElement.dataset.facetDrawerScrollY;

    removeHiddenHeaderClass();
    requestAnimationFrame(removeHiddenHeaderClass);
    window.setTimeout(removeHiddenHeaderClass, 450);
  };

  const syncFacetFiltersDrawerScrollLock = (shouldLock) => {
    if (shouldLock === false) {
      window[KEEP_OPEN_KEY] = false;
    }

    const lock = shouldLock === true || (shouldLock !== false && isFacetFiltersDrawerOpen());
    const isLocked = document.documentElement.dataset.facetDrawerScrollLocked === 'true';

    if (lock) {
      if (!isLocked) {
        document.documentElement.dataset.facetDrawerScrollY = String(window.scrollY);
        applyLock();
      }
      return;
    }

    if (isLocked) releaseLock();
  };

  const reopenFacetFiltersDrawer = () => {
    const menuDrawer =
      document.querySelector('menu-drawer.mobile-facets__wrapper .mobile-facets__disclosure') ||
      document.querySelector('menu-drawer .mobile-facets__disclosure');
    if (!menuDrawer) return;
    menuDrawer.classList.add('menu-opening');
    menuDrawer.setAttribute('open', true);
    syncFacetFiltersDrawerScrollLock(true);
  };

  const bindDisclosure = (disclosure) => {
    if (!disclosure || disclosure.dataset.facetScrollLockBound === 'true') return;
    disclosure.dataset.facetScrollLockBound = 'true';
    new MutationObserver(() => syncFacetFiltersDrawerScrollLock()).observe(disclosure, {
      attributes: true,
      attributeFilter: ['open', 'class'],
    });
  };

  const initWatchers = () => {
    document
      .querySelectorAll('menu-drawer.mobile-facets__wrapper, .mobile-facets__wrapper')
      .forEach((wrapper) => bindDisclosure(wrapper.querySelector('.mobile-facets__disclosure')));

    const results = document.querySelector('.acs_collection_results, .acs_results');
    if (results) {
      new MutationObserver(() => {
        document
          .querySelectorAll('menu-drawer.mobile-facets__wrapper, .mobile-facets__wrapper')
          .forEach((wrapper) => bindDisclosure(wrapper.querySelector('.mobile-facets__disclosure')));
        if (!window[KEEP_OPEN_KEY]) return;
        const disclosure = getDisclosure();
        if (!disclosure) return;
        if (!disclosure.hasAttribute('open') || !disclosure.classList.contains('menu-opening')) {
          reopenFacetFiltersDrawer();
        } else {
          syncFacetFiltersDrawerScrollLock(true);
        }
      }).observe(results, { childList: true, subtree: true });
    }
  };

  const lockOnFilterInteraction = (event) => {
    const target = event.target;
    if (!target?.closest) return;
    if (!target.closest('.acs_collection_results, .acs_results')) return;
    if (
      !target.matches('.mobile-facets__checkbox') &&
      !target.closest('.facets__label a, .mobile-facets__label a')
    ) {
      return;
    }
    window[KEEP_OPEN_KEY] = true;
    syncFacetFiltersDrawerScrollLock(true);
  };

  window.isFacetFiltersDrawerOpen = isFacetFiltersDrawerOpen;
  window.syncFacetFiltersDrawerScrollLock = syncFacetFiltersDrawerScrollLock;
  window.reopenFacetFiltersDrawer = reopenFacetFiltersDrawer;

  document.addEventListener('change', lockOnFilterInteraction, true);
  document.addEventListener('click', lockOnFilterInteraction, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWatchers);
  } else {
    initWatchers();
  }
})();
